import { Box, TextField, Button, Alert, Collapse } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { RestClient } from "../apis/RestClient";

interface HireMeFormValues {
  name: string;
  email: string;
  message: string;
}

const HireMe = () => {
  const [showThankYou, setShowThankYou] = useState(false);
  const formik = useFormik<HireMeFormValues>({
    initialValues: {
      name: "",
      email: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: async (
      values: HireMeFormValues,
      { resetForm }: { resetForm: () => void }
    ) => {
      try {
        await RestClient.post("/send-email", values);
        setShowThankYou(true);
        resetForm();
        setTimeout(() => setShowThankYou(false), 3000);
      } catch (error) {
        alert("Failed to send message. Please try again later.");
      }
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Collapse in={showThankYou} sx={{ width: "100%", mb: 2 }}>
        <Alert
          severity="success"
          sx={{
            fontSize: 18,
            fontWeight: 600,
            borderRadius: 2,
            justifyContent: "center",
            textAlign: "center",
            background: "linear-gradient(90deg, #f0f0f0ff 0%, #4c4e4cff 100%)",
            color: "rgba(252, 252, 252, 1)",
          }}
        >
          Thank you for reaching out! I appreciate your interest and will get
          back to you soon.
        </Alert>
      </Collapse>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: { xs: "95vw", sm: "80vw", md: "50vw", lg: "40vw" },
          maxWidth: 600,
          mx: "auto",
          mt: { xs: 4, md: 8 },
          p: { xs: 2, sm: 4 },
          borderRadius: 4,
          boxShadow: 6,
          background: "linear-gradient(135deg, #f5f7fa 0%, #9b9292ff 100%)",
          border: "none",
        }}
      >
        <Box
          component="h2"
          sx={{
            textAlign: "center",
            mb: 2,
            fontWeight: 800,
            fontSize: { xs: 24, sm: 32 },
            letterSpacing: 1,
            color: "#000",
            textShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          Hire Me
        </Box>
        <TextField
          label="Name"
          name="name"
          variant="outlined"
          required
          fullWidth
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          sx={{
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "#000",
              },
            },
          }}
        />
        <TextField
          label="Email"
          name="email"
          type="email"
          variant="outlined"
          required
          fullWidth
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          sx={{
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "#000",
              },
            },
          }}
        />
        <TextField
          label="Message"
          name="message"
          variant="outlined"
          required
          multiline
          rows={4}
          fullWidth
          value={formik.values.message}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.message && Boolean(formik.errors.message)}
          helperText={formik.touched.message && formik.errors.message}
          sx={{
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "#000",
              },
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          sx={{
            mt: 2,
            borderRadius: 3,
            fontWeight: 700,
            letterSpacing: 1,
            boxShadow: 4,
            py: 1.5,
            background: "linear-gradient(90deg, #232526 0%, #000 100%)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(90deg, #111 0%, #232526 100%)",
            },
          }}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default HireMe;
